// Parse GoHighLevel invoice CSV exports.
// Two flavors:
//   parseGHLInvoices() → staged Payment rows for the Payments tab review flow
//   parseGHLClients()  → unique Client rows deduplicated by email for the Clients tab
//
// GHL columns we read: Customer Email, Customer Name, Line Item Name,
// Invoice Total, Issue Date, Due Date, Status, Recurring Flag.
//
// Output: array of "staged" payment rows the user can review/edit
// before they're committed to the database. Each row may already be
// matched to a client (by email) and an offer (by line item name).

import { toISO } from "./dateUtils";
import { findClientByEmail, clientKeyForEmail } from "./emailUtils";

const norm = (s) => (s || "").trim().toLowerCase();

const parseAmount = (raw) => {
  if (raw == null) return 0;
  const cleaned = String(raw).replace(/[$, ]/g, "").trim();
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const parseDate = (raw) => {
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : toISO(d);
};

const mapStatus = (ghlStatus, dueDateISO, paidDateISO) => {
  const s = (ghlStatus || "").trim().toLowerCase();
  if (s === "paid") return "paid";
  if (s === "overdue") return "pending";
  if (s === "sent" || s === "draft" || s === "viewed") {
    if (dueDateISO) {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const due = new Date(dueDateISO + "T00:00:00");
      return due < today ? "pending" : "scheduled";
    }
    return "scheduled";
  }
  return "pending";
};

// Detect whether a parsed CSV looks like a GHL invoice export.
export function isGHLFormat(records) {
  if (!records || records.length === 0) return false;
  const row = records[0];
  return "Customer Email" in row || "Customer Name" in row || "Invoice number" in row;
}

// Extract unique Clients from a GHL invoice CSV — deduped by company domain
// (with exact-email fallback for generic free-email domains like gmail.com).
// Same-company emails (e.g. sales@ and accountant@ at the same domain)
// collapse into a single client.
//
// For each client, looks across all their invoices to determine:
//   - earliest Issue Date → startDate
//   - most-common "Line Item Name" → _suggestedOfferName
//   - whether ANY of their invoices were recurring → primaryType
//
// Country and type can't be inferred from GHL data, so they're left blank.
export function parseGHLClients(records) {
  const byKey = new Map(); // domain or exact-email key

  records.forEach((row) => {
    const email = norm(row["Customer Email"] || "");
    if (!email) return;
    const key = clientKeyForEmail(email);
    if (!key) return;
    const name = (row["Customer Name"] || "").trim() || email;
    const issue = parseDate(row["Issue Date"]);
    const recurring = (row["Recurring Flag"] || "").trim().toLowerCase() === "yes";
    const lineItem = (row["Line Item Name"] || "").trim();

    if (!byKey.has(key)) {
      byKey.set(key, {
        name, email, // first-seen email becomes the canonical
        country: "",
        type: "Other",
        primaryType: recurring ? "recurring" : "oneoff",
        primaryOfferId: "",
        startDate: issue || "",
        status: "active",
        acquisitionSource: "Other",
        funnel: "",
        notes: `Imported from GHL`,
        _lineItemCounts: {},
        _emailsSeen: new Set([email]),
        _anyRecurring: recurring,
      });
    }
    const c = byKey.get(key);
    c._emailsSeen.add(email);
    // Earliest invoice wins as startDate
    if (issue && (!c.startDate || issue < c.startDate)) c.startDate = issue;
    // Track line items to determine most-common offer
    if (lineItem) c._lineItemCounts[lineItem] = (c._lineItemCounts[lineItem] || 0) + 1;
    if (recurring) c._anyRecurring = true;
  });

  return [...byKey.values()].map((c) => {
    const top = Object.entries(c._lineItemCounts).sort((a, b) => b[1] - a[1])[0];
    const extraEmails = [...c._emailsSeen].filter((e) => e !== c.email);
    const notes = extraEmails.length
      ? `Imported from GHL. Also seen: ${extraEmails.join(", ")}`
      : "Imported from GHL";
    return {
      ...c,
      primaryType: c._anyRecurring ? "recurring" : "oneoff",
      _suggestedOfferName: top ? top[0] : null,
      notes,
      _lineItemCounts: undefined,
      _emailsSeen: undefined,
      _anyRecurring: undefined,
    };
  });
}

export function parseGHLInvoices({ records }, { clients, offers }) {
  const offerByName = new Map();
  offers.forEach((o) => offerByName.set(norm(o.name), o));

  return records.map((row, idx) => {
    const email = norm(row["Customer Email"] || "");
    const name = (row["Customer Name"] || "").trim();
    const lineItem = (row["Line Item Name"] || "").trim();
    const recurring = (row["Recurring Flag"] || "").trim().toLowerCase() === "yes";
    const amount = parseAmount(row["Invoice Total"] || row["Line Item Amount"]);
    const issueDate = parseDate(row["Issue Date"]);
    const dueDate = parseDate(row["Due Date"]) || issueDate;
    // Domain-aware client lookup
    const matchedClient = email ? findClientByEmail(email, clients) : null;
    const matchedOffer = lineItem ? offerByName.get(norm(lineItem)) : null;
    const status = mapStatus(row["Status"], dueDate, issueDate);

    return {
      _idx: idx,
      _ghlCustomerName: name,
      _ghlCustomerEmail: email,
      _ghlLineItem: lineItem,
      _ghlRecurring: recurring,
      _matched: !!matchedClient,
      _invoiceNumber: row["Invoice number"] || "",
      clientId: matchedClient?.id || "",
      offerId: matchedOffer?.id || "",
      amount,
      bookedDate: issueDate,
      dueDate,
      paidDate: status === "paid" ? issueDate : null,
      status,
      note: row["Invoice name"] || lineItem || "",
      // If no client match, this row will spawn a new client on commit
      _suggestClient: !matchedClient && (email || name)
        ? {
            name: name || email,
            email,
            country: "",
            type: "Other",
            primaryType: recurring ? "recurring" : "oneoff",
            primaryOfferId: "",
            startDate: issueDate || "",
            status: "active",
            acquisitionSource: "Other",
            funnel: "",
            notes: "Auto-created from GHL payment import",
          }
        : null,
    };
  });
}
